"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = getTiming;

var _get = _interopRequireDefault(require("lodash/get"));

var _isArray = _interopRequireDefault(require("lodash/isArray"));

var _isObject = _interopRequireDefault(require("lodash/isObject"));

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

/*
 * General and specific performance timing metrics that we can track.
 * Refer to https://developer.mozilla.org/en-US/docs/Web/API/Window/performance for general info.
 *
 * Additional helpful resources for understanding timing metrics:
 * - https://www.w3.org/TR/navigation-timing/#process
 * - https://www.w3.org/TR/navigation-timing-2/#process (For v2 of the timing spec)
 * - https://developers.google.com/web/fundamentals/performance/critical-rendering-path/measure-crp
 */

/**
 * getFixedPrecisionInSeconds - Converts the metric (expectation is that it is in ms), to seconds,
 * with an enforced precision of up to 3 decimal points only.
 *
 * @param  {number} metric  - the metric value in ms to convert to seconds.
 * @return {number} value of the metric in seconds, with a fixed precision of 3 decimal points.
 */
function getFixedPrecisionInSeconds(metric) {
  if (metric) {
    return +(metric / 1000).toFixed(3); // retain as a number
  }

  return metric;
}
/**
 * getTimeSinceNavStart - calculates the relative time in seconds from the Performance API
 * navigationStart for the metric in question.
 *
 * @param  {number} metric    - metric in ms from epoch
 * @param  {object} startTime - time in ms from epoch for when the browser starts processing a url
 *                              entry.
 * @return {number} relative time in seconds since navigationStart for the metric.
 */


function getTimeSinceNavStart(metric, startTime) {
  if (metric) {
    return getFixedPrecisionInSeconds(metric - startTime);
  }

  return metric;
}
/**
 * firstPaintTime - return the time from epoch to first paint for a page load (if supported on the
 * browser). This uses the Paint Timing API, which currently has limited support.
 *
 * Taken from https://developers.google.com/web/updates/2017/12/chrome-loadtimes-deprecated
 *
 * Refer to this document for more information:
 * - https://www.w3.org/TR/paint-timing/
 *
 * @param  {object} performance - the base performance API object to retrieve PerformancePaintTiming
 *                  entry data from, which contains metrics on first-paint and
 *                  first-contentful-paint.
 * @return {number} time to first paint in seconds. If the browser doesn't support the Paint Timing
 *                  API, return null.
 */


function getFirstPaintTime(performance) {
  if (window.PerformancePaintTiming) {
    var paintMetrics = performance.getEntriesByType('paint');

    if ((0, _isArray.default)(paintMetrics) && paintMetrics.length > 0) {
      return getFixedPrecisionInSeconds(paintMetrics[0].startTime);
    }
  } // Returning undefined here so the `fp` metric won't be included in the request body (whereas
  // returning null will)


  return undefined;
}
/**
 * getPerformanceNavigationTimingMetrics - returns the collection of PerformanceNavigationTiming
 * metrics to record to logs. This API is not yet fully standardized yet, although W3C recommends
 * utilizing it over the standardized/legacy PeformanceTiming API where supported.
 *
 * Refer to the following for more information:
 * - https://www.w3.org/TR/navigation-timing-2/ (working draft)
 * - https://w3c.github.io/navigation-timing/ (editor's draft)
 * - https://developer.mozilla.org/en-US/docs/Web/API/PerformanceNavigationTiming
 * - https://developer.mozilla.org/en-US/docs/Web/API/PerformanceResourceTiming
 *
 * @param  {object} performance - the base performance API object to retrieve navigation entry data
 *                  from.
 * @return {object} collection of performance metrics.
 */


function getPerformanceNavigationTimingMetrics(performance) {
  var navigationTimingMetrics = performance.getEntriesByType('navigation');

  if ((0, _isArray.default)(navigationTimingMetrics) && navigationTimingMetrics.length > 0) {
    var timing = navigationTimingMetrics[0];
    return {
      us: getFixedPrecisionInSeconds(timing.unloadEventStart),
      ue: getFixedPrecisionInSeconds(timing.unloadEventEnd),
      rds: getFixedPrecisionInSeconds(timing.redirectStart),
      rde: getFixedPrecisionInSeconds(timing.redirectEnd),
      rdc: (0, _get.default)(timing, 'redirectCount'),
      fs: getFixedPrecisionInSeconds(timing.fetchStart),
      dls: getFixedPrecisionInSeconds(timing.domainLookupStart),
      dle: getFixedPrecisionInSeconds(timing.domainLookupEnd),
      cs: getFixedPrecisionInSeconds(timing.connectStart),
      scs: getFixedPrecisionInSeconds(timing.secureConnectionStart),
      ce: getFixedPrecisionInSeconds(timing.connectEnd),
      reqs: getFixedPrecisionInSeconds(timing.requestStart),
      ress: getFixedPrecisionInSeconds(timing.responseStart),
      rese: getFixedPrecisionInSeconds(timing.responseEnd),
      dl: getFixedPrecisionInSeconds(timing.domLoading),
      fp: getFirstPaintTime(performance),
      di: getFixedPrecisionInSeconds(timing.domInteractive),
      dcls: getFixedPrecisionInSeconds(timing.domContentLoadedEventStart),
      dcle: getFixedPrecisionInSeconds(timing.domContentLoadedEventEnd),
      dc: getFixedPrecisionInSeconds(timing.domComplete),
      les: getFixedPrecisionInSeconds(timing.loadEventStart),
      lee: getFixedPrecisionInSeconds(timing.loadEventEnd)
    };
  }

  return null;
}
/**
 * getLegacyPerformanceTimingMetrics - returns the collection of PerformanceTiming metrics to record
 * to logs. This uses a standardized/legacy, backwards compatible performance API that is widely
 * supported. W3C recommends using the new PerformanceNavigationTiming API wherever possible, so
 * we should always try to reference those metrics first before using these.
 *
 * Refer to the following documents for more informations:
 * - https://www.w3.org/TR/navigation-timing/
 * - https://developer.mozilla.org/en-US/docs/Web/API/PerformanceTiming
 *
 * @param  {object} performance - the base performance API object to retrieve timing data from.
 * @return {object} collection of performance metrics.
 */


function getLegacyPerformanceTimingMetrics(performance) {
  var timing = performance.timing,
      navigation = performance.navigation;

  if ((0, _isObject.default)(timing)) {
    var startTime = timing.navigationStart;
    return {
      us: getTimeSinceNavStart(timing.unloadEventStart, startTime),
      ue: getTimeSinceNavStart(timing.unloadEventEnd, startTime),
      rds: getTimeSinceNavStart(timing.redirectStart, startTime),
      rde: getTimeSinceNavStart(timing.redirectEnd, startTime),
      rdc: (0, _get.default)(navigation, 'redirectCount'),
      fs: getTimeSinceNavStart(timing.fetchStart, startTime),
      dls: getTimeSinceNavStart(timing.domainLookupStart, startTime),
      dle: getTimeSinceNavStart(timing.domainLookupEnd, startTime),
      cs: getTimeSinceNavStart(timing.connectStart, startTime),
      scs: getTimeSinceNavStart(timing.secureConnectionStart, startTime),
      ce: getTimeSinceNavStart(timing.connectEnd, startTime),
      reqs: getTimeSinceNavStart(timing.requestStart, startTime),
      ress: getTimeSinceNavStart(timing.responseStart, startTime),
      rese: getTimeSinceNavStart(timing.responseEnd, startTime),
      dl: getTimeSinceNavStart(timing.domLoading, startTime),
      fp: getFirstPaintTime(performance),
      di: getTimeSinceNavStart(timing.domInteractive, startTime),
      dcls: getTimeSinceNavStart(timing.domContentLoadedEventStart, startTime),
      dcle: getTimeSinceNavStart(timing.domContentLoadedEventEnd, startTime),
      dc: getTimeSinceNavStart(timing.domComplete, startTime),
      les: getTimeSinceNavStart(timing.loadEventStart, startTime),
      lee: getTimeSinceNavStart(timing.loadEventEnd, startTime)
    };
  }

  return null;
}

function getTiming() {
  var performance = window.performance || window.webkitPerformance || window.msPerformance || window.mozPerformance;

  if (performance) {
    if (window.PerformanceNavigationTiming) {
      return getPerformanceNavigationTimingMetrics(performance);
    }

    return getLegacyPerformanceTimingMetrics(performance);
  }

  return null;
}